import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { fmtBRL, fmtNumber, fmtDateTime } from "../../lib/format";
import { useFilters, periodToDates } from "../../lib/filters";
import { documentsTabLabel, periodLabel, type Comparison, type MemoryTab } from "../../lib/calculation-memory/memory-tabs";
import type { CalculationMemoryDto, AuditStatus } from "@bi/types";
import { periodQuery, useMemoryAllGroups } from "./memory-data";
import { MemoryVariationTab } from "./MemoryVariationTab";
import { MemoryCompositionTab } from "./MemoryCompositionTab";
import { MemoryDocumentsTab } from "./MemoryDocumentsTab";
import { IconBarChart, IconCalculator, IconDocument, IconLineChart, IconRefresh } from "../icons";

/**
 * Cards cujo valor é a soma de um INTERVALO — só neles o período do filtro global
 * se aplica. Os demais (posição em aberto, vencido, saldo, projeção 30d, janelas
 * fixas de 30 dias) têm janela definida pela própria regra do card.
 */
const PERIOD_AWARE_CARDS = new Set([
  "recebido-no-periodo",
  "pago-no-periodo",
  "cte-receita-mes",
  "cte-count-mes",
  "cte-icms-mes",
  "cte-pedagio-mes",
  "cte-mercadoria-mes",
  "cte-quantidade-mes",
  "cte-peso-mes",
  "dre-frete-motorista",
]);

const TABS: Array<{ id: MemoryTab; label: string; icon: typeof IconCalculator }> = [
  { id: "calculo", label: "Como foi calculado", icon: IconCalculator },
  { id: "variacao", label: "Por que variou", icon: IconLineChart },
  { id: "composicao", label: "Quem compõe", icon: IconBarChart },
  { id: "documentos", label: "Documentos", icon: IconDocument },
];

// ── componente principal ──────────────────────────────────────────────────────

/**
 * Memória de cálculo de um indicador, em quatro abas (no molde da do BI da Aços
 * Litoral): como foi calculado, por que variou, quem compõe e os documentos.
 * Tudo vem do endpoint de memória de cálculo que já existia; as abas novas só
 * reorganizam e comparam esses dados na tela.
 */
export function CalculationMemoryPage() {
  const { cardKey = "" } = useParams<{ cardKey: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { filters } = useFilters();

  // Período vindo da tela de origem (?periodFrom&periodTo, ISO): é a janela INICIAL,
  // e cobre o drill por dia/faixa (ex.: uma faixa de vencimento), em que a janela não
  // é a do filtro global e precisa ser respeitada ao abrir.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFrom = searchParams.get("periodFrom");
  const urlTo = searchParams.get("periodTo");
  const tabParam = searchParams.get("aba");
  // Drill vindo de ranking (?group=) abre direto em "Quem compõe", com o grupo aberto.
  const tab: MemoryTab = TABS.some((t) => t.id === tabParam)
    ? (tabParam as MemoryTab)
    : searchParams.get("group") ? "composicao" : "calculo";
  const comparison: Comparison = searchParams.get("comp") === "ano" ? "ano" : "anterior";

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(searchParams);
    if (value == null) next.delete(key); else next.set(key, value);
    setSearchParams(next, { replace: true });
  }

  // …mas o usuário pode trocar o período DENTRO do detalhamento (a barra de filtros
  // fica visível aqui, no AppShell). Assim que o filtro global muda em relação ao que
  // veio na URL, ele passa a mandar.
  const globalPeriod = periodToDates(filters.period, filters.customFrom, filters.customTo);
  const urlPeriodIsCurrent =
    !urlFrom || !urlTo || (urlFrom === globalPeriod.from && urlTo === globalPeriod.to);
  const [pinnedUrlPeriod, setPinnedUrlPeriod] = useState(Boolean(urlFrom && urlTo));
  useEffect(() => {
    // O filtro global mudou depois de abrir → solta a janela da URL, para sempre.
    if (!urlPeriodIsCurrent) setPinnedUrlPeriod(false);
  }, [urlPeriodIsCurrent]);

  const effectiveFrom = pinnedUrlPeriod && urlFrom ? urlFrom : globalPeriod.from;
  const effectiveTo = pinnedUrlPeriod && urlTo ? urlTo : globalPeriod.to;

  // Cards de janela FIXA (a vencer 30d / vencidos 30d / posição em aberto) não têm
  // período aplicável: mandar um sobrescreveria a regra do card. O backend só usa a
  // janela em cards `date_range`, e é ele quem informa se aplicou (`appliedFilters`).
  const periodApplies = Boolean(PERIOD_AWARE_CARDS.has(cardKey) || (urlFrom && urlTo));
  const qFrom = periodApplies ? effectiveFrom : null;
  const qTo = periodApplies ? effectiveTo : null;
  const periodQS = periodQuery(qFrom, qTo);

  const { data, isLoading, isError } = useMemoryAllGroups(cardKey, qFrom, qTo);

  const reauditMutation = useMutation({
    mutationFn: () =>
      api.post<{ results: unknown[] }>(`/calculation-memory/${encodeURIComponent(cardKey)}/reaudit`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calculation-memory-all", cardKey] });
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse max-w-7xl mx-auto">
        <div className="h-8 w-64 bg-gray-200 rounded" />
        <div className="h-24 bg-gray-100 rounded-xl" />
        <div className="h-10 bg-gray-100 rounded-xl" />
        <div className="h-64 bg-gray-100 rounded-xl" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="card p-8 text-center">
        <p className="text-gray-500">Não foi possível carregar a memória de cálculo.</p>
        <button className="btn-secondary mt-4" onClick={() => navigate(-1)}>Voltar</button>
      </div>
    );
  }

  const { definition, value } = data;
  const area = location.pathname.startsWith("/operacional") ? "Operacional" : "Financeiro";
  const isPeriodCard = definition.filterType === "date_range" && Boolean(qFrom && qTo);
  const docsLabel = documentsTabLabel(cardKey);

  return (
    <div className="space-y-5 max-w-7xl mx-auto">

      {/* ── Cabeçalho ──────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={() => navigate(-1)}
            className="flex-shrink-0 p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500"
            aria-label="Voltar"
          >
            <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
            </svg>
          </button>
          <nav className="text-sm text-gray-500 truncate">
            {area} <span className="mx-1">›</span> Memória de cálculo
          </nav>
        </div>
        <div className="flex gap-2">
          <button
            className="btn-secondary text-xs py-1.5 flex items-center gap-1.5"
            onClick={() => reauditMutation.mutate()}
            disabled={reauditMutation.isPending}
          >
            <IconRefresh className={`w-3.5 h-3.5 ${reauditMutation.isPending ? "animate-spin" : ""}`} />
            {reauditMutation.isPending ? "Auditando…" : "Reauditar"}
          </button>
        </div>
      </div>

      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0 max-w-2xl">
          <h1 className="font-display font-semibold text-2xl text-gray-900">{definition.displayName}</h1>
          <p className="text-sm text-gray-600 mt-1 leading-snug">{definition.businessDefinition}</p>
        </div>
        <div className="ml-auto text-right">
          <p className="font-display font-semibold text-3xl text-gray-900 tabular-nums leading-none">
            {fmtValue(value.total, definition.displayUnit)}
          </p>
          <p className="text-xs text-gray-600 mt-1.5">
            {qFrom && qTo ? periodLabel(qFrom, qTo) : `Posição em ${fmtSyncAt(definition.appliedFilters.asOf)}`}
            {pinnedUrlPeriod && periodApplies ? " (recorte da tela de origem)" : ""}
          </p>
        </div>
      </header>

      {/* ── Abas ───────────────────────────────────────────────────────── */}
      <nav className="border-b border-surface-border flex flex-wrap gap-1" aria-label="Seções da memória de cálculo">
        {TABS.map((t) => {
          const active = tab === t.id;
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setParam("aba", t.id === "calculo" ? null : t.id)}
              aria-current={active ? "page" : undefined}
              className={`-mb-px flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors rounded-t focus:outline-none focus-visible:ring-2 focus-visible:ring-tm-500 ${
                active ? "border-tm-600 text-tm-700" : "border-transparent text-gray-600 hover:text-gray-900"
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.id === "documentos" ? docsLabel : t.label}
            </button>
          );
        })}
      </nav>

      {tab === "calculo" && <CalculationTab data={data} />}

      {tab === "variacao" && (isPeriodCard ? (
        <MemoryVariationTab
          cardKey={cardKey}
          displayName={definition.displayName}
          unit={definition.displayUnit}
          from={qFrom!}
          to={qTo!}
          comparison={comparison}
          onComparison={(c) => setParam("comp", c === "anterior" ? null : c)}
        />
      ) : (
        <div className="card p-6 max-w-2xl">
          <p className="text-sm font-semibold text-gray-900">Este número é uma posição, não a soma de um período</p>
          <p className="text-sm text-gray-600 mt-1 leading-relaxed">
            Ele mostra o que está {definition.calculationType === "overdue" ? "vencido" : definition.calculationType === "due" ? "a vencer" : "em aberto"} hoje.
            A memória de cálculo guarda só a posição atual, então não há outro momento para comparar aqui.
            A tela de origem mostra a comparação com o mês anterior, quando existe.
          </p>
        </div>
      ))}

      {tab === "composicao" && (
        <MemoryCompositionTab
          cardKey={cardKey}
          unit={definition.displayUnit}
          data={data}
          periodQS={periodQS}
          initialGroup={searchParams.get("group")}
        />
      )}

      {tab === "documentos" && (
        <MemoryDocumentsTab cardKey={cardKey} unit={definition.displayUnit} data={data} periodQS={periodQS} />
      )}
    </div>
  );
}

// ── aba "Como foi calculado" ─────────────────────────────────────────────────

function CalculationTab({ data }: { data: CalculationMemoryDto }) {
  const { definition, value, crossValidation } = data;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        {/* ── Fórmula + filtros ──────────────────────────────────────────── */}
        <div className="card p-6 space-y-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Como é calculado</p>
            <code className="block text-xs bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-gray-800 font-mono leading-relaxed">
              {definition.formulaHuman}
            </code>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Filtros aplicados</p>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">
                <span className="font-medium">Empresa:</span> {definition.appliedFilters.company}
              </span>
              {definition.filterType === "status" && (
                <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">
                  <span className="font-medium">Status:</span> Aberta
                </span>
              )}
              {definition.filterType === "date_range" && (
                <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">
                  <span className="font-medium">Período:</span>{" "}
                  {fmtYMD(definition.appliedFilters.periodFrom)}{" "}
                  a{" "}
                  {fmtYMDEndExclusive(definition.appliedFilters.periodTo)}
                </span>
              )}
              {definition.filterType === "overdue" && (
                <>
                  <span className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-700 px-2.5 py-1 rounded-full">
                    <span className="font-medium">Status:</span> Aberta
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-700 px-2.5 py-1 rounded-full">
                    <span className="font-medium">Vencimento:</span> anterior a hoje
                  </span>
                </>
              )}
              {definition.filterType === "balance" && (
                <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">
                  <span className="font-medium">Contas:</span> ativas
                </span>
              )}
              {definition.filterType === "projection" && (
                <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">
                  <span className="font-medium">Horizonte:</span>{" "}
                  {fmtYMD(definition.appliedFilters.periodFrom)}{" "}
                  a{" "}
                  {fmtYMD(definition.appliedFilters.periodTo)}
                </span>
              )}
              {definition.filterType === "due_window" && (
                <>
                  <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">
                    <span className="font-medium">Status:</span> Aberta
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">
                    <span className="font-medium">Vencimento:</span>{" "}
                    {fmtYMD(definition.appliedFilters.periodFrom)} a {fmtYMDEndExclusive(definition.appliedFilters.periodTo)}
                  </span>
                </>
              )}
              {definition.filterType === "overdue_window" && (
                <>
                  <span className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-700 px-2.5 py-1 rounded-full">
                    <span className="font-medium">Status:</span> Aberta
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-700 px-2.5 py-1 rounded-full">
                    <span className="font-medium">Vencimento:</span>{" "}
                    {fmtYMD(definition.appliedFilters.periodFrom)} a {fmtYMDEndExclusive(definition.appliedFilters.periodTo)} (vencidos)
                  </span>
                </>
              )}
              <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full">
                <span className="font-medium">Referência:</span> {fmtSyncAt(definition.appliedFilters.asOf)}
              </span>
            </div>
          </div>
        </div>

        {/* ── Valor + validação cruzada ───────────────────────────────────── */}
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* BI */}
            <div className="card p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Valor no BI</p>
              <p className="text-3xl font-bold text-gray-900">{fmtValue(value.total, definition.displayUnit)}</p>
              <p className="text-xs text-gray-400 mt-1">
                {value.recordCount.toLocaleString("pt-BR")}{" "}
                {definition.filterType === "date_range" ? "movimentos"
                  : definition.filterType === "balance" ? "contas"
                  : definition.filterType === "projection" ? "registros"
                  : "títulos"}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Sync: {fmtSyncAt(value.lastSyncAt)}
                {value.source === "sinersoft" && " · total lido do ERP ao vivo"}
              </p>
              {value.detailGapPct > 0.005 && (
                <p className="text-xs text-amber-700 mt-2">
                  {value.analyticRecordCount === 0 ? (
                    <>
                      ⚠️ O armazém não tem nenhum movimento neste período, então a composição abaixo fica vazia.
                      O total é lido do ERP ao vivo e está correto. Só o detalhamento depende da sincronização.
                    </>
                  ) : (
                    <>
                      ⚠️ Os títulos listados abaixo somam {fmtValue(value.analyticTotal, definition.displayUnit)}
                      {" "}({value.analyticRecordCount.toLocaleString("pt-BR")}). O snapshot ainda não reconciliou
                      com o ERP ({Math.round(value.detailGapPct * 1000) / 10}% de diferença). O total acima é o canônico.
                    </>
                  )}
                </p>
              )}
            </div>

            {/* Sinersoft */}
            <div className="card p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Valor no Sinersoft</p>
              {crossValidation.sourceValue !== null ? (
                <>
                  <p className="text-3xl font-bold text-gray-900">{fmtValue(crossValidation.sourceValue, definition.displayUnit)}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {crossValidation.sourceRecordCount?.toLocaleString("pt-BR") ?? "—"} registros
                  </p>
                </>
              ) : (
                <p className="text-2xl font-bold text-gray-300">—</p>
              )}
              <p className="text-xs text-gray-400 mt-1">
                Auditado: {fmtSyncAt(crossValidation.lastAuditAt)}
              </p>
            </div>
          </div>

          {/* Barra de status */}
          {statusBar(
            crossValidation.status as AuditStatus,
            crossValidation.variancePercentage,
            crossValidation.lastAuditAt,
          )}

          {crossValidation.variancePercentage !== null && crossValidation.status !== "error" && (
            <p className="text-xs text-gray-400 text-right">
              Tolerâncias do piloto: warning ≤ {crossValidation.warningTolerance}% · crítico &gt; {crossValidation.criticalTolerance}%
            </p>
          )}
        </div>
      </div>

      {/* ── Metadados do indicador: campo de data, natureza, exclusões, limitações ── */}
      <div className="lg:col-span-1">
        <div className="card p-6 space-y-4 lg:sticky lg:top-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Campo de data</p>
            <p className="text-sm text-gray-700">{definition.dateFieldLabel}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Natureza</p>
            <p className="text-sm text-gray-700">
              {{ realized: "Realizado", forecast: "Previsto (contratual)", overdue: "Vencido",
                 due: "A vencer", open: "Em aberto (posição atual)", estimated: "Estimado" }[definition.calculationType] ?? definition.calculationType}
            </p>
          </div>
          {definition.excludedStatus.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Fica de fora deste número</p>
              {definition.excludedStatus.map((e, i) => (
                <p key={i} className="text-xs text-gray-500">• {e}</p>
              ))}
            </div>
          )}
          {definition.limitations.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-600 mb-1">Limitações</p>
              {definition.limitations.map((l, i) => (
                <p key={i} className="text-xs text-amber-700">⚠ {l}</p>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────────────────────

function statusBar(status: AuditStatus, variancePct: number | null, lastAuditAt: string | null) {
  const last = lastAuditAt ? fmtDateTime(lastAuditAt) : null;

  if (status === "error") {
    return (
      <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gray-100 text-gray-500 text-sm">
        <span className="w-2 h-2 rounded-full bg-gray-400 flex-shrink-0" />
        <span>
          Validação cruzada indisponível
          {last ? `. Última validação bem-sucedida em ${last}` : ". Nenhuma validação registrada"}
        </span>
      </div>
    );
  }

  const pct = variancePct !== null ? Math.abs(variancePct).toFixed(2) : "—";

  if (status === "ok") {
    return (
      <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-green-50 text-green-700 text-sm">
        <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
        <span>Em conformidade, variação de {pct}%</span>
        {last && <span className="ml-auto text-xs text-green-600 opacity-70">Auditado em {last}</span>}
      </div>
    );
  }

  if (status === "warning") {
    return (
      <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-50 text-amber-700 text-sm">
        <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />
        <span>Atenção: variação de {pct}%</span>
        {last && <span className="ml-auto text-xs text-amber-600 opacity-70">Auditado em {last}</span>}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-50 text-red-700 text-sm">
      <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
      <span>Crítico: variação de {pct}%</span>
      {last && <span className="ml-auto text-xs text-red-600 opacity-70">Auditado em {last}</span>}
    </div>
  );
}

function fmtSyncAt(iso: string | null) {
  if (!iso) return "—";
  return fmtDateTime(iso);
}

// Parse UTC date-only string as DD/MM (avoids timezone shift from new Date("YYYY-MM-DD"))
function fmtYMD(iso: string | null): string {
  if (!iso) return "—";
  const p = iso.split("T")[0]!.split("-");
  return `${p[2]}/${p[1]}`;
}

// Same but subtracts 1 day (for exclusive end date)
function fmtYMDEndExclusive(iso: string | null): string {
  if (!iso) return "—";
  const p = iso.split("T")[0]!.split("-");
  const dt = new Date(Date.UTC(Number(p[0]), Number(p[1]) - 1, Number(p[2]) - 1));
  const d = String(dt.getUTCDate()).padStart(2, "0");
  const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
  return `${d}/${m}`;
}

function fmtValue(v: number, unit: "BRL" | "number" | undefined): string {
  if (unit === "number") return fmtNumber(v);
  return fmtBRL(v);
}
